import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import adminService, { AdminCaseDetail } from '../../services/admin.service';

const formatDate = (value: unknown): string => {
  if (!value) return '—';
  if (typeof value !== 'string') return JSON.stringify(value);
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
};

const renderValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value, null, 2);
};

const getStatusBadge = (value: string): string => {
  switch (value) {
    case 'completed':
      return 'status-badge bg-emerald-100 text-emerald-800';
    case 'in_review':
      return 'status-badge bg-amber-100 text-amber-800';
    case 'submitted':
    case 'paid':
      return 'status-badge bg-blue-100 text-blue-800';
    default:
      return 'status-badge bg-gray-100 text-gray-700';
  }
};

const getAppealText = (appealLetter: AdminCaseDetail['appealLetter']): string => {
  if (!appealLetter) return '';
  if (typeof appealLetter === 'string') return appealLetter;
  const candidateKeys = ['text', 'content', 'letter'];
  for (const key of candidateKeys) {
    const value = appealLetter[key];
    if (typeof value === 'string' && value.trim()) {
      return value;
    }
  }
  return JSON.stringify(appealLetter, null, 2);
};

const getEvidenceLink = (file: Record<string, unknown> | null): string | null => {
  if (!file) return null;
  const candidateKeys = ['downloadURL', 'url', 'signedUrl', 'publicUrl'];
  for (const key of candidateKeys) {
    const value = file[key];
    if (typeof value === 'string' && value.trim()) {
      return value;
    }
  }
  return null;
};

const getEvidenceName = (file: Record<string, unknown> | null, evidenceId: string): string => {
  if (!file) return evidenceId;
  const candidateKeys = ['originalName', 'name', 'filename', 'path'];
  for (const key of candidateKeys) {
    const value = file[key];
    if (typeof value === 'string' && value.trim()) {
      return value;
    }
  }
  return evidenceId;
};

const AdminCaseDetailPage: React.FC = () => {
  const { caseId = '' } = useParams<{ caseId: string }>();
  const [caseDetail, setCaseDetail] = useState<AdminCaseDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingStatus, setSavingStatus] = useState<'in_review' | 'completed' | null>(null);

  const loadCase = useCallback(async () => {
    if (!caseId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const data = await adminService.getCaseDetail(caseId);
      setCaseDetail(data);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load case';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    void loadCase();
  }, [loadCase]);

  const answerEntries = Object.entries(caseDetail?.answers || {});
  const ticketEntries = Object.entries(caseDetail?.ticket || {});
  const metadataEntries = Object.entries(caseDetail?.metadata || {});
  const appealText = getAppealText(caseDetail?.appealLetter || null);

  const handleCopyEmail = async () => {
    if (!caseDetail?.user.email) return;
    try {
      await navigator.clipboard.writeText(caseDetail.user.email);
      toast.success('User email copied');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to copy email');
    }
  };

  const handleStatusUpdate = async (status: 'in_review' | 'completed') => {
    if (!caseId) return;
    setSavingStatus(status);
    try {
      await adminService.updateCaseStatus(caseId, status);
      toast.success(`Case marked as ${status}`);
      await loadCase();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update case status';
      toast.error(message);
    } finally {
      setSavingStatus(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" text="Loading case details" />
      </div>
    );
  }

  if (!caseDetail) {
    return (
      <div className="min-h-screen bg-gray-50 py-10">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="rounded-lg border border-gray-200 bg-white p-12 text-center shadow-sm">
            <h1 className="text-2xl font-bold text-gray-900">Case not found</h1>
            <p className="mt-3 text-gray-600">This case could not be loaded from the current review queue.</p>
            <Link to="/admin/cases" className="btn-primary mt-6 inline-flex px-4 py-2 text-sm">
              Back to cases
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-10">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <Link to="/admin/cases" className="text-sm font-medium text-[#8E6D3B] hover:underline">
              ← Back to cases
            </Link>
            <h1 className="mt-3 text-3xl font-bold text-gray-900">Case {caseDetail.caseId}</h1>
            <p className="mt-2 text-gray-600">Internal human-review view for ticket data, supporting answers, letter output, and evidence.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleCopyEmail}
              disabled={!caseDetail.user.email}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Copy user email
            </button>
            <button
              type="button"
              onClick={() => void handleStatusUpdate('in_review')}
              disabled={savingStatus !== null}
              className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {savingStatus === 'in_review' ? 'Saving…' : 'Mark in review'}
            </button>
            <button
              type="button"
              onClick={() => void handleStatusUpdate('completed')}
              disabled={savingStatus !== null}
              className="btn-primary px-4 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
            >
              {savingStatus === 'completed' ? 'Saving…' : 'Mark completed'}
            </button>
          </div>
        </div>

        <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-4">
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-sm text-gray-500">User email</p>
            <p className="mt-2 break-all font-medium text-gray-900">{caseDetail.user.email || '—'}</p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-sm text-gray-500">Plan type</p>
            <p className="mt-2 font-medium text-gray-900">{caseDetail.planType || '—'}</p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-sm text-gray-500">Payment status</p>
            <p className="mt-2">
              <span className={getStatusBadge(caseDetail.paymentStatus)}>{caseDetail.paymentStatus || 'unknown'}</span>
            </p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-sm text-gray-500">Case status</p>
            <p className="mt-2">
              <span className={getStatusBadge(caseDetail.caseStatus)}>{caseDetail.caseStatus || 'unknown'}</span>
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900">Ticket information</h2>
            <dl className="mt-4 space-y-3">
              {ticketEntries.length === 0 ? (
                <div className="text-sm text-gray-500">No ticket data available.</div>
              ) : (
                ticketEntries.map(([key, value]) => (
                  <div key={key} className="grid grid-cols-1 gap-1 border-b border-gray-100 pb-3 last:border-b-0 last:pb-0 sm:grid-cols-[160px_1fr]">
                    <dt className="text-sm font-medium text-gray-500">{key}</dt>
                    <dd className="whitespace-pre-wrap break-words text-sm text-gray-900">{renderValue(value)}</dd>
                  </div>
                ))
              )}
            </dl>
          </section>

          <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900">Submitted answers</h2>
            <dl className="mt-4 space-y-3">
              {answerEntries.length === 0 ? (
                <div className="text-sm text-gray-500">No question-answer data available.</div>
              ) : (
                answerEntries.map(([key, value]) => (
                  <div key={key} className="grid grid-cols-1 gap-1 border-b border-gray-100 pb-3 last:border-b-0 last:pb-0 sm:grid-cols-[160px_1fr]">
                    <dt className="text-sm font-medium text-gray-500">{key}</dt>
                    <dd className="whitespace-pre-wrap break-words text-sm text-gray-900">{renderValue(value)}</dd>
                  </div>
                ))
              )}
            </dl>
          </section>
        </div>

        <section className="mt-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900">Generated appeal letter</h2>
          <div className="mt-4 rounded-lg bg-gray-50 p-4">
            <pre className="whitespace-pre-wrap break-words font-sans text-sm text-gray-900">
              {appealText || 'No appeal letter available.'}
            </pre>
          </div>
        </section>

        <section className="mt-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900">Uploaded evidence</h2>
          {caseDetail.evidence.length === 0 ? (
            <p className="mt-4 text-sm text-gray-500">No evidence items found for this case.</p>
          ) : (
            <div className="mt-4 space-y-4">
              {caseDetail.evidence.map((item) => {
                const link = getEvidenceLink(item.file);
                return (
                  <div key={item.evidenceId} className="rounded-lg border border-gray-200 p-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <p className="font-medium text-gray-900">{getEvidenceName(item.file, item.evidenceId)}</p>
                        <p className="mt-1 text-sm text-gray-500">
                          {item.category || 'uncategorized'} · {item.status || 'unknown'} · added {formatDate(item.createdAt)}
                        </p>
                      </div>
                      {link ? (
                        <a
                          href={link}
                          target="_blank"
                          rel="noreferrer"
                          className="text-sm font-medium text-[#8E6D3B] hover:underline"
                        >
                          Open file
                        </a>
                      ) : null}
                    </div>
                    {item.summary ? <p className="mt-3 text-sm text-gray-700">{item.summary}</p> : null}
                    {item.extracted ? (
                      <details className="mt-3">
                        <summary className="cursor-pointer text-sm font-medium text-gray-700">Extracted data</summary>
                        <pre className="mt-2 overflow-x-auto rounded bg-gray-50 p-3 text-xs text-gray-800">
                          {JSON.stringify(item.extracted, null, 2)}
                        </pre>
                      </details>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <section className="mt-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900">Metadata</h2>
          <dl className="mt-4 space-y-3">
            {metadataEntries.length === 0 ? (
              <div className="text-sm text-gray-500">No metadata available.</div>
            ) : (
              metadataEntries.map(([key, value]) => (
                <div key={key} className="grid grid-cols-1 gap-1 border-b border-gray-100 pb-3 last:border-b-0 last:pb-0 sm:grid-cols-[200px_1fr]">
                  <dt className="text-sm font-medium text-gray-500">{key}</dt>
                  <dd className="whitespace-pre-wrap break-words text-sm text-gray-900">
                    {key.toLowerCase().includes('at') ? formatDate(value) : renderValue(value)}
                  </dd>
                </div>
              ))
            )}
          </dl>
        </section>
      </div>
    </div>
  );
};

export default AdminCaseDetailPage;
